import { computed } from '@ember/object';
import { equal, alias, gt } from '@ember/object/computed';
import { isIn } from '@square/dashboard-utilities/macros';
import Enum from '@square/dashboard-utilities/utils/enum';
import DateTime from '@square/dashboard-data/models/squareup/common/time/date-time';
import YearMonthDay from '@square/dashboard-data/models/squareup/common/time/year-month-day';
import MoneyProto from '@square/dashboard-data/models/squareup/common/money-proto';
import moment from 'moment';
import { ProtoBaseModel, attr, repeated } from '@square/dashboard-data/utils/ember-model-stubs';
export const Category = new Enum(['UNKNOWN', 'ACH_CREDIT_NOT_PROCESSED', 'ACH_INSUFFICIENT_FUNDS', 'ACH_UNAUTHORIZED', 'AMOUNT_DIFFERS', 'CANCELLED', 'COMPLIANCE', 'DISSATISFIED', 'DUPLICATE', 'EMV_LIABILITY_SHIFT', 'FRAUD', 'NO_KNOWLEDGE', 'NOT_AS_DESCRIBED', 'NOT_RECEIVED', 'PAID_BY_OTHER_MEANS', 'CUSTOMER_REQUESTS_CREDIT', 'UNAUTHORIZED'
// if you add an entry here, also add to translations
// under activity.transactions.details.disputes.pastTense and leave this comment last :)
]);
export const Resolution = new Enum(['R_UNKNOWN', 'NEW_DISPUTE', 'UNDER_REVIEW', 'ACCEPTED', 'PENDING_RESOLUTION', 'REVERSED', 'LOST', 'WON', 'DISPUTE_REOPENED']);
export const ProtectionState = new Enum(['PS_UNKNOWN', 'PROTECTED', 'NOT_PROTECTED', 'PROTECTION_WAIVED', 'PENDING_PROTECTION_DECISION', 'NOT_APPLICABLE', 'VIRGIL', 'MANUAL_VIRGIL', 'VIRGIL_EXPIRED']);
export const ActionableStatus = new Enum(['UNKNOWN', 'ACTIONABLE', 'NOT_ACTIONABLE', 'NO_ACTION_DISPUTED_AMOUNT_TOO_LOW', 'NO_ACTION_REFUNDED', 'VIEWABLE']);
export const RequestType = new Enum(['UNKNOWN', 'INITIAL_REQUEST', 'ADDITIONAL_REQUEST']);
const UNRESOLVED_STATES = [Resolution.NEW_DISPUTE, Resolution.UNDER_REVIEW, Resolution.PENDING_RESOLUTION, Resolution.DISPUTE_REOPENED];
const RESOLVED_STATES = [Resolution.ACCEPTED, Resolution.LOST, Resolution.WON];
const InformationRequest = ProtoBaseModel.extend({
  formToken: attr(String),
  formUrl: attr(String),
  requestType: attr(RequestType),
  actionableStatus: attr(ActionableStatus),
  requestedAt: attr(DateTime),
  customerDueAt: attr(DateTime),
  completedAt: attr(DateTime),
  dashboardForm: attr(Boolean),
  isActionable: equal('actionableStatus', ActionableStatus.ACTIONABLE),
  isViewable: equal('actionableStatus', ActionableStatus.VIEWABLE)
});
export default ProtoBaseModel.extend({
  paymentToken: attr(String),
  disputeReason: attr(Category),
  bankDisputedComment: attr(String),
  latestReportingDate: attr(YearMonthDay),
  informationRequest: repeated(InformationRequest),
  informationRequests: alias('informationRequest'),
  currentAmountHeld: attr(MoneyProto),
  currentDisputedAmount: attr(MoneyProto),
  resolution: attr(Resolution),
  protectionState: attr(ProtectionState),
  currentActionableStatus: attr(ActionableStatus),
  submittedToBankAt: attr(DateTime),
  resolutionDecidedAt: attr(DateTime),
  expectedResolutionDate: attr(YearMonthDay),
  paymentCreatedAt: attr(DateTime),
  cardholderName: attr(String),
  unitToken: attr(String),
  isCadDispute: equal('currentDisputedAmount.currencyCode', 'CAD'),
  isUnknown: equal('disputeReason', Category.UNKNOWN),
  hasCurrentAmountHeld: gt('currentAmountHeld.amount', 0),
  isNoActionDisputedAmountTooLow: equal('currentActionableStatus', ActionableStatus.NO_ACTION_DISPUTED_AMOUNT_TOO_LOW),
  isNoActionRefunded: equal('currentActionableStatus', ActionableStatus.NO_ACTION_REFUNDED),
  // resolution states
  isNewDispute: equal('resolution', Resolution.NEW_DISPUTE),
  isUnderReview: equal('resolution', Resolution.UNDER_REVIEW),
  isAccepted: equal('resolution', Resolution.ACCEPTED),
  isPendingResolution: equal('resolution', Resolution.PENDING_RESOLUTION),
  isReversed: equal('resolution', Resolution.REVERSED),
  isLost: equal('resolution', Resolution.LOST),
  isWon: equal('resolution', Resolution.WON),
  isReopened: equal('resolution', Resolution.DISPUTE_REOPENED),
  isResolved: isIn('resolution', RESOLVED_STATES),
  isUnresolved: isIn('resolution', UNRESOLVED_STATES),
  isActionable: equal('currentActionableStatus', ActionableStatus.ACTIONABLE),
  isPastResolutionDate: computed('isUnresolved', 'expectedResolutionDate', function () {
    return moment(this.expectedResolutionDate).diff(moment(), 'days') < 0 && this.isUnresolved;
  }),
  // protection states
  isProtected: equal('protectionState', ProtectionState.PROTECTED),
  isNotProtected: equal('protectionState', ProtectionState.NOT_PROTECTED),
  isPendingProtectionDecision: equal('protectionState', ProtectionState.PENDING_PROTECTION_DECISION),
  isDeferredHold: isIn('protectionState', [ProtectionState.VIRGIL, ProtectionState.MANUAL_VIRGIL]),
  isDeferredHoldExpired: equal('protectionState', ProtectionState.VIRGIL_EXPIRED),
  isProtectionEligible: isIn('protectionState', [ProtectionState.PROTECTED, ProtectionState.PENDING_PROTECTION_DECISION])
});